import type { ListBoxOption } from '@components/molecules';

const YEAR_MONTH_DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export type ParsedMaxDate = {
	year: number;
	month: number;
	day: number;
};

/**
 * Parses a YYYY-MM-DD max date string into numeric year/month/day parts.
 * Returns null when the value is missing or not a valid calendar date string.
 */
export const parseMaxDate = (maxDate?: string): ParsedMaxDate | null => {
	if (!maxDate) {
		return null;
	}

	const match = YEAR_MONTH_DAY_PATTERN.exec(maxDate);
	if (!match) {
		return null;
	}

	const [, yearStr, monthStr, dayStr] = match;
	const year = Number(yearStr);
	const month = Number(monthStr);
	const day = Number(dayStr);

	if (!year || !month || !day) {
		return null;
	}

	return { year, month, day };
};

/**
 * Whether calendar parts exceed a parsed max date (undefined parts are treated as not beyond).
 */
export const isDatePartsBeyondMax = (
	year: number | undefined,
	month: number | undefined,
	day: number | undefined,
	maxDate: ParsedMaxDate | null
): boolean => {
	if (!maxDate || year === undefined) {
		return false;
	}

	if (year > maxDate.year) {
		return true;
	}

	if (year < maxDate.year || month === undefined) {
		return false;
	}

	if (month > maxDate.month) {
		return true;
	}

	if (month < maxDate.month || day === undefined) {
		return false;
	}

	return day > maxDate.day;
};

/**
 * Filters year options so none exceed the max date year.
 */
export const filterYearOptionsByMaxDate = (
	yearsOptions: ListBoxOption[],
	maxDate: ParsedMaxDate | null
): ListBoxOption[] => {
	if (!maxDate) {
		return yearsOptions;
	}

	return yearsOptions.filter(option => Number(option.value) <= maxDate.year);
};

/**
 * Filters month options when the selected year equals the max date year.
 */
export const filterMonthOptionsByMaxDate = (
	monthsOptions: ListBoxOption[],
	selectedYear: number | undefined,
	maxDate: ParsedMaxDate | null
): ListBoxOption[] => {
	if (!maxDate || selectedYear !== maxDate.year) {
		return monthsOptions;
	}

	return monthsOptions.filter(option => Number(option.value) <= maxDate.month);
};

export const calculateDaysInMonth = (
	month: number = new Date().getMonth() + 1,
	year: number = new Date().getFullYear()
): ListBoxOption[] => {
	const daysInMonth: number = new Date(year, month, 0).getDate();

	return new Array(daysInMonth).fill(0).map((_, index) => ({
		label: String(index + 1),
		value: index + 1,
	}));
};

/**
 * Builds day options for the selected month/year, capped by max date when applicable.
 */
export const filterDaysOptionsByMaxDate = (
	month: number | undefined,
	year: number | undefined,
	maxDate: ParsedMaxDate | null,
	daysOptions?: ListBoxOption[]
): ListBoxOption[] => {
	const baseDays = daysOptions ?? calculateDaysInMonth(month, year);

	if (!maxDate || year !== maxDate.year || month !== maxDate.month) {
		return baseDays;
	}

	return baseDays.filter(option => Number(option.value) <= maxDate.day);
};

export const MONTHS_OPTIONS = [
	{
		label: 'January',
		value: 1,
	},
	{
		label: 'February',
		value: 2,
	},
	{
		label: 'March',
		value: 3,
	},
	{
		label: 'April',
		value: 4,
	},
	{
		label: 'May',
		value: 5,
	},
	{
		label: 'June',
		value: 6,
	},
	{
		label: 'July',
		value: 7,
	},
	{
		label: 'August',
		value: 8,
	},
	{
		label: 'September',
		value: 9,
	},
	{
		label: 'October',
		value: 10,
	},
	{
		label: 'November',
		value: 11,
	},
	{
		label: 'December',
		value: 12,
	},
];

export const YEAR_OPTIONS_LENGTH = 100;
export const CURRENT_YEAR: number = new Date().getFullYear();
export const MIN_YEAR: number = CURRENT_YEAR - YEAR_OPTIONS_LENGTH;

export const YEARS_OPTIONS = new Array(YEAR_OPTIONS_LENGTH + 1)
	.fill(0)
	.map((_, i) => {
		const year: number = MIN_YEAR + i;
		return {
			label: String(year),
			value: year,
		};
	})
	.reverse();
